'use client';

import {
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
  parsePhoneNumber,
} from 'libphonenumber-js';
import React, { useEffect, useMemo, useState, useId, useRef } from 'react';
import { Box, Text, Flex } from '@radix-ui/themes';
import { IconCheck, IconChevronDown } from '@tabler/icons-react';
import { cn } from '@trashlab/sitesapp/src/@/lib/utils';

type CountryCode = ReturnType<typeof getCountries>[number];

// Get country flag emoji from country code
const getCountryFlag = (countryCode: CountryCode): string => {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Format phone number based on country code
const formatPhoneNumber = (digits: string, countryCode: CountryCode | 'OTHER'): string => {
  // Remove all non-digits
  const cleanDigits = digits.replace(/\D/g, '');

  switch (countryCode) {
    case 'US':
    case 'CA': {
      // Format: +1 (XXX) XXX-XXXX
      const areaCode = cleanDigits.slice(0, 3);
      const middle = cleanDigits.slice(3, 6);
      const last = cleanDigits.slice(6, 10);

      if (cleanDigits.length === 0) return '+1 ';
      if (cleanDigits.length <= 3) return `+1 (${areaCode}`;
      if (cleanDigits.length <= 6) return `+1 (${areaCode}) ${middle}`;
      return `+1 (${areaCode}) ${middle}-${last}`;
    }
    case 'MX': {
      // Format: +52 (XXX) XXX-XXXX
      const areaCode = cleanDigits.slice(0, 3);
      const middle = cleanDigits.slice(3, 6);
      const last = cleanDigits.slice(6, 10);

      if (cleanDigits.length === 0) return '+52 ';
      if (cleanDigits.length <= 3) return `+52 (${areaCode}`;
      if (cleanDigits.length <= 6) return `+52 (${areaCode}) ${middle}`;
      return `+52 (${areaCode}) ${middle}-${last}`;
    }
    case 'GB': {
      // Format: +44 XXXX XXXXXX
      const first = cleanDigits.slice(0, 4);
      const second = cleanDigits.slice(4, 10);

      if (cleanDigits.length === 0) return '+44 ';
      if (cleanDigits.length <= 4) return `+44 ${first}`;
      return `+44 ${first} ${second}`;
    }
    case 'IN': {
      // Format: +91 XXXXX XXXXX
      const first = cleanDigits.slice(0, 5);
      const second = cleanDigits.slice(5, 10);

      if (cleanDigits.length === 0) return '+91 ';
      if (cleanDigits.length <= 5) return `+91 ${first}`;
      return `+91 ${first} ${second}`;
    }
    case 'UA': {
      // Format: +380 (XX) XXX-XX-XX
      const areaCode = cleanDigits.slice(0, 2);
      const part1 = cleanDigits.slice(2, 5);
      const part2 = cleanDigits.slice(5, 7);
      const part3 = cleanDigits.slice(7, 9);

      if (cleanDigits.length === 0) return '+380 ';
      if (cleanDigits.length <= 2) return `+380 (${areaCode}`;
      if (cleanDigits.length <= 5) return `+380 (${areaCode}) ${part1}`;
      if (cleanDigits.length <= 7) return `+380 (${areaCode}) ${part1}-${part2}`;
      return `+380 (${areaCode}) ${part1}-${part2}-${part3}`;
    }
    case 'UY': {
      // Format: +598 X XXX XX XX
      const first = cleanDigits.slice(0, 1);
      const second = cleanDigits.slice(1, 4);
      const third = cleanDigits.slice(4, 6);
      const fourth = cleanDigits.slice(6, 8);

      if (cleanDigits.length === 0) return '+598 ';
      if (cleanDigits.length <= 1) return `+598 ${first}`;
      if (cleanDigits.length <= 4) return `+598 ${first} ${second}`;
      if (cleanDigits.length <= 6) return `+598 ${first} ${second} ${third}`;
      return `+598 ${first} ${second} ${third} ${fourth}`;
    }
    case 'BR': {
      // Format: +55 (XX) XXXXX-XXXX
      const areaCode = cleanDigits.slice(0, 2);
      const middle = cleanDigits.slice(2, 7);
      const last = cleanDigits.slice(7, 11);

      if (cleanDigits.length === 0) return '+55 ';
      if (cleanDigits.length <= 2) return `+55 (${areaCode}`;
      if (cleanDigits.length <= 7) return `+55 (${areaCode}) ${middle}`;
      return `+55 (${areaCode}) ${middle}-${last}`;
    }
    case 'BG': {
      // Format: +359 (XXX) XXX-XXX
      const areaCode = cleanDigits.slice(0, 3);
      const middle = cleanDigits.slice(3, 6);
      const last = cleanDigits.slice(6, 9);

      if (cleanDigits.length === 0) return '+359 ';
      if (cleanDigits.length <= 3) return `+359 (${areaCode}`;
      if (cleanDigits.length <= 6) return `+359 (${areaCode}) ${middle}`;
      return `+359 (${areaCode}) ${middle}-${last}`;
    }
    case 'TH': {
      // Format: +66 XXX XXX XXX
      const first = cleanDigits.slice(0, 3);
      const second = cleanDigits.slice(3, 6);
      const third = cleanDigits.slice(6, 9);

      if (cleanDigits.length === 0) return '+66 ';
      if (cleanDigits.length <= 3) return `+66 ${first}`;
      if (cleanDigits.length <= 6) return `+66 ${first} ${second}`;
      return `+66 ${first} ${second} ${third}`;
    }
    case 'OTHER':
    default: {
      // Format: +XXX XXX XXX XXXX
      const countryCodeDigits = cleanDigits.slice(0, 3);
      const part1 = cleanDigits.slice(3, 6);
      const part2 = cleanDigits.slice(6, 9);
      const part3 = cleanDigits.slice(9, 13);

      if (cleanDigits.length === 0) return '+';
      if (cleanDigits.length <= 3) return `+${countryCodeDigits}`;
      if (cleanDigits.length <= 6) return `+${countryCodeDigits} ${part1}`;
      if (cleanDigits.length <= 9) return `+${countryCodeDigits} ${part1} ${part2}`;
      return `+${countryCodeDigits} ${part1} ${part2} ${part3}`;
    }
  }
};

// Get max digits for a country
const getMaxDigits = (countryCode: CountryCode | 'OTHER'): number => {
  switch (countryCode) {
    case 'US':
    case 'CA':
    case 'MX':
      return 10;
    case 'GB':
    case 'IN':
      return 10;
    case 'UA':
      return 9;
    case 'UY':
      return 8;
    case 'BR':
      return 11;
    case 'BG':
    case 'TH':
      return 9;
    case 'OTHER':
    default:
      return 13;
  }
};

interface PhoneNumberInputProps {
  value?: string;
  onChange?: (value: string) => void;
  description?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  autoFocus?: boolean;
  label?: string;
  required?: boolean;
  name?: string;
  id?: string;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
}

export const PhoneNumberInput = ({
  value,
  onChange,
  description,
  disabled = false,
  readOnly = false,
  error,
  autoFocus = false,
  label = 'Phone number',
  required = false,
  name,
  id: propsId,
  placeholder: propsPlaceholder,
  className,
  containerClassName,
}: PhoneNumberInputProps) => {
  const generatedId = useId();
  const id = propsId || generatedId;

  const [selectedCountry, setSelectedCountry] = useState<CountryCode | 'OTHER'>(
    'US',
  );
  const [phoneNumber, setPhoneNumber] = useState<string>(() => {
    if (!value) {
      return formatPhoneNumber('', 'US');
    }

    try {
      const parsed = parsePhoneNumber(value);
      const country = parsed.country && isSupportedCountry(parsed.country)
        ? parsed.country as CountryCode
        : 'US';
      return formatPhoneNumber(parsed.nationalNumber, country);
    } catch {
      return formatPhoneNumber('', 'US');
    }
  });
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Track the last value we sent to onChange to prevent useEffect from interfering during typing
  const lastOnChangeValueRef = useRef<string>('');

  const countryNames: Record<string, string> = {
    US: 'United States',
    CA: 'Canada',
    GB: 'United Kingdom',
    IN: 'India',
    MX: 'Mexico',
    UA: 'Ukraine',
    UY: 'Uruguay',
    BR: 'Brazil',
    BG: 'Bulgaria',
    TH: 'Thailand',
    OTHER: 'Other',
  };

  const countryCodes = useMemo(() => {
    // Only include countries that have specific masks defined
    const supportedCountries = [
      'US',
      'CA',
      'GB',
      'IN',
      'MX',
      'UA',
      'UY',
      'BR',
      'BG',
      'TH',
    ] as CountryCode[];

    const allCountries = supportedCountries.map((country) => ({
      value: country,
      flag: getCountryFlag(country),
      code: getCountryCallingCode(country),
      name: countryNames[country] || country,
    }));

    // Define priority countries in order
    const priorityCountries = ['US', 'CA', 'MX'];

    // Separate priority countries from the rest
    const priority: typeof allCountries = [];
    const rest: typeof allCountries = [];

    allCountries.forEach((country) => {
      if (priorityCountries.includes(country.value)) {
        priority.push(country);
      } else {
        rest.push(country);
      }
    });

    // Sort priority countries by the defined order
    priority.sort(
      (a, b) =>
        priorityCountries.indexOf(a.value) - priorityCountries.indexOf(b.value),
    );

    // Sort the rest alphabetically by country name
    rest.sort((a, b) => a.name.localeCompare(b.name));

    // Combine priority countries first, then the rest
    return [...priority, ...rest];
  }, []);

  useEffect(() => {
    // Skip if this is just our own onChange value coming back - prevents interfering during typing
    if (value === lastOnChangeValueRef.current) {
      return;
    }

    if (value) {
      try {
        const parsedNumber = parsePhoneNumber(value);
        if (parsedNumber.country && isSupportedCountry(parsedNumber.country)) {
          const country = parsedNumber.country as CountryCode;
          setSelectedCountry(country);
          // Format the phone number with the country mask
          setPhoneNumber(formatPhoneNumber(parsedNumber.nationalNumber, country));
        }
      } catch (error) {
        //ignore - partial phone numbers during typing will fail to parse
      }
    } else if (!lastOnChangeValueRef.current) {
      // Only clear if we didn't just send a value (avoids clearing during backspace)
      setPhoneNumber(formatPhoneNumber('', selectedCountry));
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleCountryChange = (countryValue: string) => {
    if (
      countryValue &&
      (isSupportedCountry(countryValue) || countryValue === 'OTHER')
    ) {
      const newCountry = countryValue as CountryCode | 'OTHER';

      // If country is actually changing, reset the phone number
      if (newCountry !== selectedCountry) {
        const formattedEmpty = formatPhoneNumber('', newCountry);
        setPhoneNumber(formattedEmpty);
        // Send empty string to form when country changes (clearing the phone)
        lastOnChangeValueRef.current = '';
        if (onChange) {
          onChange('');
        }
      }

      setSelectedCountry(newCountry);
      setOpen(false);
    }
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    // Extract only digits from input
    const allDigits = inputValue.replace(/\D/g, '');

    // Strip the country calling code from the beginning
    const countryCallingCode = selectedCountry !== 'OTHER'
      ? getCountryCallingCode(selectedCountry)
      : '';

    let nationalDigits = allDigits;
    if (countryCallingCode && allDigits.startsWith(countryCallingCode)) {
      nationalDigits = allDigits.slice(countryCallingCode.length);
    }

    // Limit to max digits for the country
    const maxDigits = getMaxDigits(selectedCountry);
    const limitedDigits = nationalDigits.slice(0, maxDigits);

    // Format the phone number for display
    const formattedValue = formatPhoneNumber(limitedDigits, selectedCountry);

    // Store the formatted value for display
    setPhoneNumber(formattedValue);

    if (onChange) {
      // Send empty string if no digits, otherwise send the formatted value
      // This ensures validation works correctly (empty = no phone entered)
      const valueForForm = limitedDigits.length === 0 ? '' : formattedValue;
      lastOnChangeValueRef.current = valueForForm;
      onChange(valueForForm);
    }
  };

  const selectedCountryData = countryCodes.find(
    (c) => c.value === selectedCountry,
  );

  return (
    <Box className={cn('space-y-2', containerClassName)}>
      <Text as="label" size="2" className="block" htmlFor={id}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Text>
      <Flex>
        <Box className="relative" ref={dropdownRef}>
          <button
            type="button"
            className="flex h-[42px] w-[70px] items-center justify-between whitespace-nowrap rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 rounded-r-none border-r-0"
            disabled={disabled || readOnly}
            onClick={() => setOpen(!open)}
          >
            <span className="text-[18px]">{selectedCountryData?.flag}</span>
            <IconChevronDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
          </button>
          {open && (
            <div className="absolute top-full left-0 mt-1 w-[220px] bg-white border border-gray-200 rounded-md shadow-lg z-[300] max-h-[200px] overflow-auto">
              {countryCodes.map((country) => (
                <button
                  key={country.value}
                  type="button"
                  className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-100 text-left"
                  onClick={() => handleCountryChange(country.value)}
                >
                  <Flex align="center" gap="2">
                    <span>{country.flag}</span>
                    <span>{country.name}</span>
                    <span className="text-gray-500">+{country.code}</span>
                  </Flex>
                  {selectedCountry === country.value && (
                    <IconCheck className="h-4 w-4 text-blue-500" />
                  )}
                </button>
              ))}
            </div>
          )}
        </Box>
        <Box className="relative flex-1">
          <input
            value={phoneNumber}
            onChange={handlePhoneChange}
            disabled={disabled}
            readOnly={readOnly}
            id={id}
            name={name}
            type="tel"
            className={cn(
              'flex h-[42px] w-full rounded-md border border-gray-300 bg-white px-3 py-1 text-base shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50 rounded-l-none',
              error && 'border-red-500',
              className,
            )}
            autoFocus={autoFocus}
            placeholder={propsPlaceholder || '+1 (555) 555-5555'}
          />
        </Box>
      </Flex>
      {description && (
        <Text size="1" color="gray">
          {description}
        </Text>
      )}
      {error && (
        <Text size="1" color="red">
          {error}
        </Text>
      )}
    </Box>
  );
};

export default PhoneNumberInput;
